import { useLazyQuery } from "@apollo/client";
import { forwardRef, useEffect, useId, useImperativeHandle, useState } from "react";
import { BsArrowReturnRight } from "react-icons/bs";
import { GET_CHILD_ORGS, GET_GLOBAL_ROOT_ORGS, GET_ROOT_ORGS } from "../../../graphql/org-gql";
import { ICategory, IChildOrgsResponse, IGlobalRootOrgsResponse, IOrg, IRootOrgsResponse, isCategory } from "../../../types/org";
import Loading from "../loading";
import OmsToolTip from "../tooltip/tooltip";
import { TreeCollapseIcon, TreeExpandIcon } from "../icons";
import useAuth from "../../../hook/useAuth";

type OrgTreeItemProps = {
    category?: ICategory;
    org?: IOrg;
    isGlobal: boolean;
    isSelected: (item: ICategory | IOrg) => boolean;
    onSelected: (item: ICategory | IOrg) => void;
    selectOrgOnly?: boolean; // expand/collapse the category instead of selecting it
    orgPathIds: number[] | undefined;
}
const OrgTreeItem = forwardRef((props: OrgTreeItemProps,ref:React.Ref<any>) => {
    const {category, org, isGlobal, isSelected, onSelected, selectOrgOnly, orgPathIds} = props;
    const [expanded, setExpanded] = useState<boolean>(false)
    const [getRootOrgs, {loading: rootLoading, data: rootData }] = useLazyQuery<IRootOrgsResponse>(GET_ROOT_ORGS)
    const [getGlobalRootOrgs, {loading: globalRootLoading, data: globalRootData }] = useLazyQuery<IGlobalRootOrgsResponse>(GET_GLOBAL_ROOT_ORGS)

    const [getChildOrgs, {loading, data: childData }] = useLazyQuery<IChildOrgsResponse>(GET_CHILD_ORGS,{fetchPolicy: 'network-only'/*,onCompleted:(data)=>{console.log("child orgs:",data)}*/})
    const {isAdmin, isCoordinator, isFrenchTranslator} = useAuth();
    const toggleCategory = (category: ICategory) => {
        if (!expanded) {
            if (isGlobal) {
                getGlobalRootOrgs({ variables: { categoryId: category.id}})
            } else {
                getRootOrgs({ variables: { categoryId: category.id}/*,onCompleted:(data)=>{console.log("root orgs:",data)}*/})
            }
        }
        setExpanded(!expanded)
    }

    const toggleOrg = (org: IOrg) => {
        if (!expanded) {
            getChildOrgs({ variables: { id: org.id}})
        }
        setExpanded(!expanded)
    }

    const oid = useId();

    useImperativeHandle(ref, () => ({
        refresh() {
            setExpanded(false)
        }
    }))

    const onInputKeyDown = (event: any, item: ICategory | IOrg) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            event.stopPropagation()
            onSelected(item);
        }
        if (event.key === 'ArrowRight') {
            event.preventDefault()
            event.stopPropagation()

            if (!expanded) {
                // only attempt to expand if there are child items
                const treeItem = event.currentTarget.closest('li[role="treeitem"]') as HTMLLIElement;
                if(treeItem.classList.contains('branch')) {
                    isCategory(item) ?  toggleCategory(item) :   toggleOrg(item)
                }
            }
        }
        if (event.key === 'ArrowLeft') {
            event.preventDefault()
            event.stopPropagation()

            if (expanded) {
                isCategory(item) ?  toggleCategory(item) :   toggleOrg(item)
            }
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            event.stopPropagation()

            findNextSibling(event)
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault()
            event.stopPropagation()

            findPrevSibling(event)
        }
    }

    const skipEscape = (event: any) => {
        if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
        }
    }

    const findNextSibling = (event: any) => {
        const treeItem = event.currentTarget.closest('li[role="treeitem"]') as HTMLLIElement;
        // if the current item is expanded, select the first child
        if (treeItem.getAttribute('aria-expanded') === 'true') {
            const firstChild = treeItem
                .children[treeItem.children.length - 1] // ul is always the last child
                .children[0] as HTMLLIElement; // li items
            
            focusInput(firstChild);
        }
        else {
            // not expanded, see if we have a sibling
            if (treeItem.nextElementSibling) {
                // we have a next sibling, select it
                focusInput(treeItem.nextElementSibling);
            }
            else {
                // no next sibling, see if the current item is expanded
                // if so, go to the first child, otherwise go to the parent's next sibling (if there is one)
                focusNextParent(treeItem);
            }
        }
    }

    const findPrevSibling = (event: any) => {
        const treeItem = event.currentTarget.closest('li[role="treeitem"]') as HTMLLIElement;
        if (treeItem.previousElementSibling) {
            // has a previous sibling, see if its expanded
            if (treeItem.previousElementSibling.getAttribute('aria-expanded') === 'true') {
                // expanded, find the last visible child
                focusLastVisibleChild(treeItem.previousElementSibling as HTMLLIElement);
            }
            else {
                // not expanded, select the previous sibling
                focusInput(treeItem.previousElementSibling);
            }
        }
        else {
            // no previous siblings, target the parent (if not at the root)
            var parentList = treeItem.parentNode as HTMLUListElement;
            if (parentList && parentList.getAttribute('role') === 'group') {
                // has a parent, focus it
                focusInput(parentList.parentNode);
            }
            // no else block, if the previous check evaluates to false, we're on the top level, and there are no previous siblings
        }
    }

    const focusLastVisibleChild = (treeItem: HTMLLIElement) => {
        const childItems = treeItem
            .children[treeItem.children.length - 1] // ul is always the last child
            .children; // li items

        // get the last item in the list, see if its expanded
        const lastChildItem = childItems[childItems.length - 1] as HTMLLIElement;
        if (lastChildItem.getAttribute('aria-expanded') === 'true') {
            // expanded, find the last visible child
            focusLastVisibleChild(lastChildItem);
        }
        else {
            // not expanded, select the previous sibling
            focusInput(lastChildItem);
        }
    }

    const focusNextParent = (treeItem: HTMLLIElement) => {
        const parentList = treeItem.parentNode as HTMLUListElement;
        // go to the parent's next sibling, if it exists
        if (parentList.parentNode instanceof HTMLLIElement) {
            const parentBranch = parentList.parentNode as HTMLLIElement;
            if (parentBranch.nextElementSibling) {
                // parent node has a next sibling
                focusInput(parentBranch.nextElementSibling as HTMLLIElement);
            }
            else {
                // have to go up a level
                focusNextParent(parentBranch);
            }
        }
        // no else block - if the previous check evaluates to false, we're on the last item of the top level items, no where else to focus
    }

    const focusInput = (listItem: any) => {
        if (listItem) {
            let treeItemTitleChild = listItem.querySelector('.tree-item-title')
            if(treeItemTitleChild){
                treeItemTitleChild.focus()
            }
        }
    }

    const getRootData = () => {
        return isGlobal ? globalRootData?.globalRootOrgs : rootData?.rootOrgs
    }

    useEffect(() => {
        orgPathIds && orgPathIds.length > 0 && org && checkOrg(org)
        orgPathIds && orgPathIds.length > 0 && category && checkCategory(category)
      }, [org, category, orgPathIds]);

    const checkOrg =(item: IOrg) =>{
        if(orgPathIds && orgPathIds.includes(item.id)){
            toggleOrg(item)
        }
    }

    const checkCategory =(item: ICategory ) =>{
        if (orgPathIds?.some(id => Math.abs(id) == item.id)) {
            toggleCategory(item);
          }
    }

    function generateOrgDisplayName(org: IOrg) : string {
        if (isCoordinator && org.orgAcronym && org.orgAcronym.length > 0) {           
            return `${org.orgName} (${org.orgAcronym})`
        }
        // default to just the orgName
        return org.orgName;
    }

    return (<>
        {org && <li role="treeitem" aria-expanded={expanded} className={ `${org?.hasChild ? 'branch' : 'leaf'} treeitem-org` }>

            { org?.hasChild ?
            <button type="button" id={`${oid}_toggle`} onClick={()=>toggleOrg(org)} className="treeitem-icon treeitem-expand-collapse" aria-label={expanded ? "collapse" : "expand"}>
                {expanded ? <TreeCollapseIcon /> : <TreeExpandIcon />}
            </button>
            :
            <span className="treeitem-icon"><BsArrowReturnRight /></span>
            }
            <button type="button" className = {`tree-item-title ${isSelected(org) ? 'selected': ''}`} id={`${oid}_item`} onClick={()=>onSelected(org)} 
                onKeyDown={(e) => onInputKeyDown(e, org)} onKeyUp={skipEscape}>
                <OmsToolTip id={`${oid}_tooltip`} orgId={org.id}>
                    {generateOrgDisplayName(org)}
                </OmsToolTip>
            </button>

            { expanded && loading && <Loading style={{ paddingLeft: '2rem' }} /> }
            { expanded && !loading && 
                 <ul role="group">
                    {childData?.childOrgs?.map(c => <OrgTreeItem key={c.id} org={c} isGlobal={isGlobal} onSelected={onSelected} isSelected={isSelected} orgPathIds={orgPathIds}></OrgTreeItem>)}
                </ul>
            }
        </li>
        }

        {category && 
        <li role="treeitem" aria-expanded={expanded} className="branch treeitem-category">
            <button type="button" id={`${oid}_toggle`} className="treeitem-icon treeitem-expand-collapse" onClick={()=>toggleCategory(category)}  aria-label={expanded ? "collapse" : "expand"}>
                {expanded ? <TreeCollapseIcon /> : <TreeExpandIcon />}
            </button>
            <button type="button" className={`tree-item-title ${!selectOrgOnly && isSelected(category) ? 'selected': ''}`} id={`${oid}_item`} 
                onClick={() => selectOrgOnly ? toggleCategory(category) : onSelected(category)}
                onKeyDown={(e) => e.key === 'Enter' && selectOrgOnly ? toggleCategory(category) : onInputKeyDown(e, category)} 
                onKeyUp={skipEscape}>
                {category.categoryName}
            </button>
            { expanded && (rootLoading || globalRootLoading) && <Loading style={{ paddingLeft: '2rem' }} /> }
            { expanded && !(rootLoading || globalRootLoading) &&
                <ul role="group">
                    {getRootData()?.map(c => <OrgTreeItem key={c.id} org={c} isGlobal={isGlobal} onSelected={onSelected} isSelected={isSelected} orgPathIds={orgPathIds}></OrgTreeItem>)}
                </ul>
            }    
            </li>
        }
        </>
    )
})

export default OrgTreeItem;
